import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Drawer } from '@ygtechnologies/rubus-ui';
import { classnames, LoadingIndicator, Sticky } from '@ygtechnologies/bygr-frontend-component-library';
import { faCircleInfo, faFilter } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

import theme from './Room.module.scss';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { RoomOptionMenu } from './components/RoomOptionMenu';
import { RoomOptionCards, GroupProductPreview } from './components/RoomOptionTwoTierList';
import { RoomImage } from './components/RoomImage';
import { RoomCategoryFilter } from './components/RoomCategoryFilter';
import { STICKY_OFFSET } from './constants';
import { ConfiguratorGroupDto } from '@/services/configurator/index';
import { RoomStyle } from './RoomStyle';
import {
    buildGroupOptionVisualizationUrls,
    buildVisualizationUrl,
    preloadImages,
} from '@/services/configurator-visualization';
import { ROOM_PLACEHOLDER_IMAGE_URL } from '../roomPlaceholderImage';

const MOBILE_QUERY = '(max-width: 500px)';

// Immersive rooms without a 3D visualization now use the visualized layout with a placeholder image
// (see `showPlaceholderViz` below) so both room types present identically. Flip this to `true` to
// restore the previous centered card-grid layout — kept intact so it can come back, or be promoted to
// a project feature toggle, without rebuilding it. See `RoomOptionCards`.
const USE_CARD_GRID_FALLBACK = false;

function useMediaMatch(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(query).matches
            : false
    );

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        setMatches(mql.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [query]);

    return matches;
}

export interface RoomProps { }

export const Room: React.FC<RoomProps> = () => {
    const { roomId, deadlineId } = useParams<{ roomId: string; deadlineId?: string }>();
    const { is3d, immersive, tabsHeight, configuratorData, rooms, visualizationRooms, visualizationLoading } =
        useOptionConfigurator();

    const currentRoomId = Number(roomId);

    const allGroups: ConfiguratorGroupDto[] = useMemo(() => {
        const room = rooms.find((r) => r.roomId === currentRoomId) ?? null;
        return room?.groups ?? [];
    }, [rooms, currentRoomId]);

    const vizRoom = useMemo(
        () => visualizationRooms?.find((r) => r.roomId === currentRoomId) ?? null,
        [visualizationRooms, currentRoomId]
    );
    const visualizationUrl = useMemo(
        () => (vizRoom ? buildVisualizationUrl(vizRoom, allGroups) : null),
        [vizRoom, allGroups]
    );

    const hasViz = !!(vizRoom && visualizationUrl);
    // The synthetic "General" bucket (groups with no room) isn't a physical room, so a room image
    // would misrepresent it — it keeps the card grid.
    const isGeneralRoom = currentRoomId === 0;
    const showCardGrid = immersive && (USE_CARD_GRID_FALLBACK || isGeneralRoom);
    // Immersive room with no visualization: render the visualized layout anyway, with a placeholder
    // standing in for the render, so it doesn't look like a different product.
    const showPlaceholderViz = immersive && !hasViz && !showCardGrid;
    // The image shown in the visual panel — the live visualization, or the placeholder. Null when the
    // room has no visual panel at all (card grid / the legacy non-immersive layout).
    const panelImageUrl = hasViz ? visualizationUrl : showPlaceholderViz ? ROOM_PLACEHOLDER_IMAGE_URL : null;

    // Track image loading whenever the panel image url changes (initial render + every
    // param/selection change); cleared once the new image finishes loading.
    const [vizLoading, setVizLoading] = useState(true);
    const [showVizSpinner, setShowVizSpinner] = useState(true);
    const vizLoadedOnceRef = useRef(false);
    // Natural aspect ratio (width / height) of the loaded visualization, captured from the preload
    // image below. Used on mobile to box the visual panel to the image's proportions (full width,
    // auto height) instead of the desktop fixed-height crop. Stable per room's visualization canvas.
    const [vizAspectRatio, setVizAspectRatio] = useState<number | null>(null);

    // Drive the loading state from a standalone Image() rather than the rendered <img>'s onLoad.
    // The rendered element lives inside react-zoom-pan-pinch and, when the source is already cached
    // (the visuals are preloaded, and SPA-navigating into the showroom warms the cache), its onLoad
    // can fire before React attaches the handler — leaving the spinner stuck. A standalone loader
    // settles deterministically, including the synchronous `complete` cache hit. The browser dedupes
    // the request, so the visible <img> still paints from the same fetch.
    useEffect(() => {
        if (!panelImageUrl) return;
        setVizLoading(true);
        let cancelled = false;
        const settle = () => {
            if (cancelled) return;
            vizLoadedOnceRef.current = true;
            setVizLoading(false);
            // Capture the image's natural aspect ratio for the mobile aspect-ratio box (guard 0/0).
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setVizAspectRatio(img.naturalWidth / img.naturalHeight);
            }
        };
        const img = new Image();
        img.onload = settle;
        img.onerror = settle;
        img.src = panelImageUrl;
        // Already cached: `complete` is true synchronously and no load event will follow.
        if (img.complete) settle();
        return () => {
            cancelled = true;
        };
    }, [panelImageUrl]);

    // Show the spinner immediately on the very first load, but defer it by 500ms on subsequent
    // configuration changes — quick image updates then swap in place without flashing a spinner.
    useEffect(() => {
        if (!vizLoading) {
            setShowVizSpinner(false);
            return;
        }
        if (!vizLoadedOnceRef.current) {
            setShowVizSpinner(true);
            return;
        }
        setShowVizSpinner(false);
        const timer = setTimeout(() => setShowVizSpinner(true), 500);
        return () => clearTimeout(timer);
    }, [vizLoading]);

    const handleVizLoaded = () => {
        vizLoadedOnceRef.current = true;
        setVizLoading(false);
    };

    // Pre-load the visuals for every option of a group as soon as it's expanded, so picking an option
    // swaps the image in instantly. Loaded in batches of max 5 to avoid flooding the network.
    const handleGroupExpand = useCallback(
        (group: ConfiguratorGroupDto) => {
            if (!vizRoom) return;
            preloadImages(buildGroupOptionVisualizationUrls(vizRoom, allGroups, group), 5);
        },
        [vizRoom, allGroups]
    );

    const [filteredGroups, setFilteredGroups] = useState<ConfiguratorGroupDto[]>([]);
    // The group currently open in the visualized two-tier menu — used to overlay the visual panel
    // for groups that have no 3D-visualization mapping (selecting them wouldn't change the image).
    const [activeVizGroup, setActiveVizGroup] = useState<ConfiguratorGroupDto | null>(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const toggleFilterModal = useCallback(() => setIsFilterModalOpen((open) => !open), []);
    const isMobile = useMediaMatch(MOBILE_QUERY);

    // Measure the visual panel so we can tell when a wide image is being cropped on its sides (image
    // aspect wider than the panel). In that case the image is shown at full height + natural width and
    // can be panned left/right to reveal the hidden sides (see RoomImage `coverPan`). Device-agnostic:
    // it's driven by the actual geometry, so it kicks in on landscape tablets, narrow desktop panels,
    // panoramic images, etc. — and stays off when the image already fits (stacked layout, tall images).
    const vizPanelRef = useRef<HTMLDivElement>(null);
    const [vizPanelAspect, setVizPanelAspect] = useState<number | null>(null);
    useEffect(() => {
        const el = vizPanelRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const measure = () => {
            const { clientWidth: w, clientHeight: h } = el;
            if (w > 0 && h > 0) setVizPanelAspect(w / h);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [panelImageUrl]);

    // Image cropped horizontally (wider than the panel) → enable cover-with-pan. Only when the image is
    // displayed as a cropping cover (immersive); the auth layout shows it contained (no crop). A small
    // margin avoids flip-flopping when the two ratios are near-equal.
    const imageCroppedHorizontally =
        vizAspectRatio != null && vizPanelAspect != null && vizAspectRatio > vizPanelAspect * 1.02;
    const vizCoverPan = immersive && imageCroppedHorizontally;

    useEffect(() => {
        if (!isMobile && isFilterModalOpen) {
            setIsFilterModalOpen(false);
        }
    }, [isMobile, isFilterModalOpen]);

    const handleFilteredGroupsChange = useCallback((groups: ConfiguratorGroupDto[]) => {
        setFilteredGroups(groups);
    }, []);

    const containerStyle = { '--tabs-height': `${tabsHeight}px` } as React.CSSProperties;
    const stickyOffset = STICKY_OFFSET + tabsHeight;

    const roomStyles = configuratorData?.styleGroups.filter((x) => x.roomId === currentRoomId) || [];

    useEffect(() => {
        const key = `option-config-scroll:${deadlineId ?? 'none'}:${roomId}`;
        const savedY = sessionStorage.getItem(key);
        if (savedY != null) {
            const y = Number(savedY);
            if (!Number.isNaN(y)) {
                requestAnimationFrame(() => {
                    window.scrollTo(0, y);
                });
            }
        }

        return () => {
            sessionStorage.setItem(key, String(window.scrollY));
        };
    }, [deadlineId, roomId]);

    // The unit's visualization data is fetched up-front by the provider. Until it settles we don't yet
    // know whether this room is visualized, so hold with a neutral loader rather than rendering the
    // list — which would otherwise flash away the instant the data arrives and the visual panel takes
    // over. The fetch starts at provider mount, so by the time a room mounts this is usually already
    // resolved and this branch never paints.
    if (visualizationLoading) {
        return (
            <div
                className={classnames(theme.roomContainer, theme.roomLoading, { [theme.showroom]: immersive })}
                style={containerStyle}>
                <LoadingIndicator />
            </div>
        );
    }

    // Visualized layout: the visual panel (live render, or the placeholder for a room without one)
    // next to the options panel.
    if (panelImageUrl) {
        // The open group, resolved live from context (so its selection is current).
        const liveActiveGroup = activeVizGroup ? allGroups.find((g) => g.id === activeVizGroup.id) ?? null : null;
        const activeSelectedOption = liveActiveGroup
            ? liveActiveGroup.selectedOption ?? liveActiveGroup.options.find((o) => o.selected) ?? null
            : null;
        // The room's 3D-visualization mapping for the open group, if any. The visual can't reflect a
        // choice in two cases — (a) the group itself has no mapping, or (b) the group is mapped but
        // the selected item has no mapping entry (resolves to "missing"). In both, overlay the visual
        // panel with the product preview / prompt, exactly as the non-visual layout does. A room on
        // the placeholder has no mappings at all, so every open group overlays the placeholder.
        const activeGroupMapping = liveActiveGroup
            ? vizRoom?.mappings.find((m) => m.optionGroupId === liveActiveGroup.id) ?? null
            : null;
        const selectedItemMapped =
            !!activeGroupMapping &&
            activeSelectedOption != null &&
            activeGroupMapping.options.some((o) => o.optionItemId === activeSelectedOption.id);
        const showProductOverlay =
            !!liveActiveGroup && (!activeGroupMapping || (activeSelectedOption != null && !selectedItemMapped));

        return (
            <div
                className={classnames(theme.roomContainer, theme.roomViz, { [theme.showroom]: immersive })}
                style={
                    {
                        ...containerStyle,
                        // Consumed only by the mobile `.visualPanel` rule to box the panel to the
                        // image's natural proportions (full width, auto height). Desktop ignores it.
                        ...(vizAspectRatio ? { '--viz-aspect-ratio': String(vizAspectRatio) } : {}),
                    } as React.CSSProperties
                }>
                <Card className={classnames(theme.visualPanel, { [theme.visualPanelCover]: immersive })}>
                    <div ref={vizPanelRef} className={theme.vizPanelMeasure}>
                        <RoomImage
                            roomUrl={panelImageUrl}
                            roomConfig={{ name: hasViz ? t`3D configurator` : t`Room preview` }}
                            showLoadingIndicator={showVizSpinner}
                            onImageLoad={handleVizLoaded}
                            onImageError={handleVizLoaded}
                            // When the image is wider than the panel its sides are cropped; let it pan
                            // left/right to reveal them (y-axis locked). Driven by measured geometry.
                            coverPan={vizCoverPan}
                            // The placeholder isn't a render of the current selection — showing it
                            // static keeps it from being inspected as if it were.
                            interactive={hasViz}
                        />
                    </div>
                    {/* The placeholder is generic imagery, not a render of this unit's selections —
                        say so, so nobody reads it as their configuration. Dropped while the product
                        preview covers the panel (nothing of the image is visible to caption). */}
                    {showPlaceholderViz && !showProductOverlay && (
                        <div className={theme.placeholderHint}>
                            <FontAwesomeIcon icon={faCircleInfo} className={theme.placeholderHintIcon} />
                            <span>
                                <Trans>
                                    Example visualization — it does not reflect your current selection.
                                </Trans>
                            </span>
                        </div>
                    )}
                    {showProductOverlay && liveActiveGroup && (
                        <GroupProductPreview option={activeSelectedOption} groupName={liveActiveGroup.name} />
                    )}
                </Card>
                <Card className={theme.optionsPanel}>
                    <RoomOptionMenu
                        groups={allGroups}
                        onGroupExpand={handleGroupExpand}
                        onActiveGroupChange={setActiveVizGroup}
                    />
                </Card>
            </div>
        );
    }

    if (is3d) {
        return (
            <div
                className={classnames(theme.roomContainer, theme.room3d, { [theme.showroom]: immersive })}
                style={containerStyle}>
                <Sticky offsetTop={stickyOffset}>
                    <Card className={theme.rightPanel}>
                        <RoomOptionMenu groups={filteredGroups} is3d={true} />
                    </Card>
                </Sticky>
            </div>
        );
    }

    const filterContent = (
        <>
            <RoomCategoryFilter groups={allGroups} onFilteredGroupsChange={handleFilteredGroupsChange} />
            {/* Immersive + showroom render styles in the group-list "Styles" section (see
                RoomStyleSection); only the legacy auth layout keeps the inline style cards here. */}
            {!immersive && <RoomStyle stylesGroups={roomStyles} />}
        </>
    );

    // Immersive card grid — now only the "General" bucket, plus every non-visualized room when
    // USE_CARD_GRID_FALLBACK is flipped on. A centered card grid of option groups (and the room's
    // styles), capped to a comfortable width — no side filter panel. Opening a group card reveals its
    // options in a side panel with the selected-product preview (side-by-side on desktop, stacked with
    // the preview on top on narrow screens — see RoomOptionCards). Handles all widths. Uses the full
    // group list (`allGroups`) since the category filter sidebar is gone.
    if (showCardGrid) {
        return (
            <div className={classnames(theme.roomContainer, theme.showroom)} style={containerStyle}>
                <RoomOptionCards groups={allGroups} onGroupExpand={handleGroupExpand} />
            </div>
        );
    }

    return (
        <div
            className={classnames(theme.roomContainer, { [theme.showroom]: immersive })}
            style={containerStyle}>
            {isMobile && (
                <div className={theme.filterButtonRow}>
                    <Button variant="tertiary" icon={faFilter} onClick={toggleFilterModal}>
                        <Trans>Filter</Trans>
                    </Button>
                </div>
            )}
            <Card className={theme.leftPanel}>
                <RoomOptionMenu groups={filteredGroups} />
            </Card>
            {!isMobile && <div className={theme.rightPanel}>{filterContent}</div>}
            {isMobile && (
                <Drawer
                    className={theme.mobileFilterDrawer}
                    isOpen={isFilterModalOpen}
                    toggle={toggleFilterModal}
                    onClose={() => setIsFilterModalOpen(false)}
                    closeOnClickOutside
                    closeOnEscape
                    showCloseButton={false}
                    size="lg">
                    <div className={theme.mobileFilterDrawerContent}>{filterContent}</div>
                    <div className={theme.mobileFilterDrawerFooter}>
                        <Button variant="primary" onClick={toggleFilterModal}>
                            <Trans>Close</Trans>
                        </Button>
                    </div>
                </Drawer>
            )}
        </div>
    );
};
